import { useEffect } from 'react';
import {
  Bot, Cpu, FlaskConical, BookOpen,
  ArrowRight, MapPin, Phone, Mail, ChevronRight, Code2, Zap
} from 'lucide-react';

const GF = 'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&family=Space+Mono:wght@400;700&display=swap';

const PROGRAMS = [
  { code: 'PRG_001', name: 'Early STEM (KG–V)',     desc: 'Logical thinking, Scratch coding, hands-on science.', tags: ['SCRATCH', 'MATH_GAMES', 'EXPERIMENTS'] },
  { code: 'PRG_002', name: 'Tech Core (VI–VIII)',    desc: 'Python, 3D printing, electronics, IoT projects.', tags: ['PYTHON', 'ARDUINO', '3D_PRINT'] },
  { code: 'PRG_003', name: 'Board Excellence (IX–X)',desc: 'CBSE prep with mock tests and AI-powered dashboards.', tags: ['CBSE', 'AI_TUTOR', 'MOCK_TESTS'] },
  { code: 'PRG_004', name: 'Science + CS (XI–XII)',  desc: 'PCM/PCB with Computer Science and research pathways.', tags: ['JEE', 'NEET', 'ML_PROJECTS'] },
  { code: 'PRG_005', name: 'Commerce + Fintech',     desc: 'Business studies paired with financial technology.', tags: ['FINTECH', 'STARTUP', 'DATA'] },
  { code: 'PRG_006', name: 'Robotics & AI Club',     desc: 'After-school club for national/international olympiads.', tags: ['ROS', 'TENSORFLOW', 'COMPETITIONS'] },
];

const FACILITIES = [
  { num: '01', icon: Bot,         name: 'AI & Machine Learning Lab', desc: 'GPU-powered workstations and TensorFlow environments for Grade IX+.' },
  { num: '02', icon: Cpu,         name: 'Robotics Workshop',         desc: 'Full-scale lab with Arduino, Raspberry Pi, and industrial robotic arms.' },
  { num: '03', icon: FlaskConical, name: 'Digital Science Labs',     desc: 'Physics, Chemistry, Biology with digital sensors and simulation software.' },
  { num: '04', icon: BookOpen,    name: '24/7 Smart Library',        desc: '50,000-title digital library with AI-powered reading recommendations.' },
];

export default function TechTheme({ school }) {
  const loginUrl = `/login?school=${school.slug}`;

  useEffect(() => {
    const link = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: GF });
    document.head.appendChild(link);
    document.title = `${school.name} | EduPulse`;
    return () => document.head.removeChild(link);
  }, [school.name]);

  const TICKER_ITEMS = ['AI & Machine Learning Lab', 'Robotics Programme', `${school.board} Affiliated`,
    'Smart Classrooms', 'Coding from Grade I', '24/7 Digital Library', 'International Olympiads'];

  const admissions = [
    { key: 'Application deadline', val: `31 May ${new Date().getFullYear()}` },
    { key: 'Entrance test',        val: `15 June ${new Date().getFullYear()}` },
    { key: 'Grades accepting',     val: `${school.academic?.grades?.[0] || 'I'} → ${school.academic?.grades?.[school.academic?.grades?.length - 1] || 'XII'}` },
    { key: 'Board',                val: school.board },
  ];

  return (
    <div className="font-grotesk bg-tech-bg text-white overflow-x-hidden"
      style={{ backgroundImage: 'linear-gradient(rgba(0,102,255,0.04) 1px,transparent 1px),linear-gradient(90deg,rgba(0,102,255,0.04) 1px,transparent 1px)', backgroundSize: '60px 60px' }}>

      {/* ── NAV ── */}
      <nav className="fixed top-0 inset-x-0 z-50 bg-tech-bg/85 backdrop-blur-xl border-b border-white/7 h-[66px] flex items-center justify-between px-13">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-tech-blue flex items-center justify-center font-mono font-bold text-[12px] text-white"
            style={{ clipPath: 'polygon(50% 0%,100% 50%,50% 100%,0% 50%)' }}>
            {school.name[0]}
          </div>
          <div>
            <div className="font-mono font-bold text-[13px] text-white tracking-[0.04em]">{school.name}</div>
            <div className="font-mono text-[10px] text-tech-blue-b tracking-[0.08em]">
              // {school.board} · Est. {school.establishedYear}
            </div>
          </div>
        </div>
        <ul className="hidden md:flex gap-8 list-none">
          {['Overview', 'Programmes', 'Labs', 'Admissions'].map(l => (
            <li key={l}>
              <a href={`#${l.toLowerCase()}`}
                className="text-[13px] font-medium text-white/45 hover:text-tech-cyan transition-colors no-underline tracking-[0.03em]">
                {l}
              </a>
            </li>
          ))}
        </ul>
        <a href={loginUrl}
          className="text-[13px] font-bold text-white bg-tech-blue px-5 py-2 rounded-md hover:bg-tech-blue-b transition-all no-underline">
          Login →
        </a>
      </nav>

      {/* ── HERO ── */}
      <section className="min-h-screen relative flex flex-col justify-center px-13 pt-36 pb-24 overflow-hidden" id="overview">
        {/* glows */}
        <div className="absolute top-[10%] left-[-10%] w-[700px] h-[700px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle,rgba(0,102,255,0.15),transparent 65%)' }} />
        <div className="absolute bottom-0 right-[-5%] w-[500px] h-[500px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle,rgba(0,212,255,0.08),transparent 65%)' }} />

        <div className="inline-flex items-center gap-2 border border-tech-blue/40 bg-tech-blue/8 text-tech-blue-b font-mono text-[11px] px-3.5 py-1.5 rounded mb-9 w-fit tracking-[0.08em]">
          ▶ ADMISSIONS OPEN · SESSION {new Date().getFullYear()}–{String(new Date().getFullYear() + 1).slice(2)}
        </div>

        <h1 className="font-grotesk font-bold text-[clamp(52px,7vw,92px)] leading-[1.0] text-white mb-2 max-w-4xl relative z-10">
          Code the <span className="text-tech-blue-b">Future.</span><br />
          Build the <span className="text-tech-cyan">World.</span>
        </h1>

        <p className="font-mono text-[14px] text-tech-cyan tracking-[0.05em] mb-7">
          // {school.tagline}
        </p>

        <p className="text-[17px] text-white/45 leading-[1.8] max-w-xl mb-12 font-light relative z-10">
          {school.name} — India's most forward-thinking {school.board} school — combines
          rigorous academics with hands-on technology, robotics, and AI literacy from Grade I.
        </p>

        <div className="flex gap-3.5 items-center mb-20 relative z-10 flex-wrap">
          <a href={loginUrl}
            className="inline-flex items-center gap-2 font-bold text-[14px] bg-tech-blue text-white px-7 py-3.5 rounded-lg hover:bg-tech-blue-b hover:shadow-[0_0_30px_rgba(0,102,255,0.4)] hover:-translate-y-0.5 transition-all no-underline">
            Login to Portal <ArrowRight size={16} />
          </a>
          <a href="#admissions"
            className="text-[14px] font-semibold text-white/45 border border-white/7 px-7 py-3.5 rounded-lg hover:border-tech-cyan/30 hover:text-tech-cyan transition-all no-underline">
            View Admissions
          </a>
        </div>

        <div className="flex gap-0 relative z-10 flex-wrap">
          {[
            { val: school.stats.results, label: 'Board results' },
            { val: school.stats.students, label: 'Students enrolled' },
            { val: `${school.stats.staff}+`, label: 'Faculty members' },
          ].map((m, i) => (
            <div key={i} className="pr-10 mr-10 border-r border-white/7 last:border-r-0 last:mr-0">
              <div className="font-mono font-bold text-[32px] text-white leading-none">
                {m.val}<span className="text-[18px] text-tech-blue-b" />
              </div>
              <div className="text-[12px] text-white/20 mt-1 tracking-[0.05em]">{m.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── TICKER ── */}
      <div className="bg-tech-blue py-3 overflow-hidden whitespace-nowrap">
        <div className="inline-block animate-ticker">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((item, i) => (
            <span key={i} className="font-mono text-[12px] text-white/80 tracking-[0.08em] uppercase px-8 after:content-['//'] after:text-white/30 after:ml-8">
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* ── PROGRAMS ── */}
      <section className="py-24 px-13 relative z-10" id="programmes">
        <div className="mb-16">
          <div className="flex items-center gap-2.5 font-mono text-[11px] text-tech-blue-b tracking-[0.15em] uppercase mb-4">
            <Code2 size={14} /> PROGRAMMES
          </div>
          <h2 className="font-grotesk font-bold text-[clamp(34px,3.5vw,52px)] text-white leading-[1.15] max-w-xl">
            Built for the <span className="text-tech-cyan">builders</span> of tomorrow
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-white/7 border border-white/7">
          {PROGRAMS.map(p => (
            <div key={p.code}
              className="bg-tech-card p-10 hover:bg-tech-mid transition-all relative overflow-hidden group cursor-default">
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-transparent group-hover:bg-tech-blue transition-colors" />
              <div className="font-mono text-[11px] text-tech-blue-b tracking-[0.1em] mb-5">{p.code}</div>
              <div className="font-grotesk font-bold text-[22px] text-white mb-3">{p.name}</div>
              <p className="text-[14px] text-white/45 leading-[1.7] mb-7">{p.desc}</p>
              <div className="flex flex-wrap gap-2">
                {p.tags.map(t => (
                  <span key={t} className="font-mono text-[10px] text-white/20 border border-white/7 px-2 py-1 tracking-[0.05em]">{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── STATS ── */}
      <section className="bg-tech-mid border-y border-white/7 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-white/7">
          {[
            { val: school.stats.results,                            lbl: 'Board Pass Rate' },
            { val: `${new Date().getFullYear() - school.establishedYear}+`, lbl: 'Years of Innovation' },
            { val: `${school.stats.staff}+`,                        lbl: 'Faculty Members' },
            { val: school.stats.students,                           lbl: 'Students Enrolled' },
          ].map((s, i) => (
            <div key={i} className="px-10 py-10 text-center">
              <div className="font-mono font-bold text-[52px] text-tech-cyan leading-none">{s.val}</div>
              <div className="text-[13px] text-white/20 mt-2.5 tracking-[0.05em]">{s.lbl}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FACILITIES + ADMISSIONS ── */}
      <section className="py-24 px-13 relative z-10" id="admissions">
        <div className="grid md:grid-cols-2 gap-16 items-start">

          {/* facilities */}
          <div>
            <div className="flex items-center gap-2.5 font-mono text-[11px] text-tech-blue-b tracking-[0.15em] uppercase mb-4">
              <Zap size={14} /> INFRASTRUCTURE
            </div>
            <h2 className="font-grotesk font-bold text-[clamp(34px,3.5vw,52px)] text-white leading-[1.15] mb-10">
              Built for <span className="text-tech-cyan">deep work</span>
            </h2>
            <div className="flex flex-col gap-0.5">
              {FACILITIES.map(({ num, icon: Icon, name, desc }) => (
                <div key={num}
                  className="border border-white/7 p-7 flex gap-4 items-start hover:border-tech-blue/30 hover:bg-tech-blue/4 transition-all cursor-default">
                  <div className="font-mono text-[12px] text-tech-blue-b min-w-[28px] mt-0.5">{num}</div>
                  <div>
                    <div className="flex items-center gap-2 font-grotesk font-semibold text-[16px] text-white mb-1.5">
                      <Icon size={15} className="text-tech-blue-b" /> {name}
                    </div>
                    <p className="text-[13px] text-white/45 leading-[1.7]">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* admissions panel */}
          <div className="border border-white/7 bg-tech-card p-10">
            <div className="font-mono text-[12px] text-tech-blue-b tracking-[0.1em] mb-6">
              // ADMISSIONS {new Date().getFullYear()}–{String(new Date().getFullYear() + 1).slice(2)}
            </div>
            {/* code block */}
            <div className="bg-tech-bg border border-white/7 p-6 font-mono text-[13px] leading-[1.8] rounded mb-5 text-white/45">
              <span className="text-white/20">{'/* Application Status */'}</span><br />
              <span className="text-tech-blue-b">const</span> admissions = {'{'}<br />
              &nbsp;&nbsp;status: <span className="text-tech-cyan">"OPEN"</span>,<br />
              &nbsp;&nbsp;school: <span className="text-tech-cyan">"{school.name}"</span>,<br />
              &nbsp;&nbsp;board: <span className="text-tech-cyan">"{school.board}"</span>,<br />
              &nbsp;&nbsp;seats: <span className="text-tech-cyan">"LIMITED"</span><br />
              {'}'}
            </div>
            {/* details list */}
            <div className="flex flex-col">
              {admissions.map((r, i) => (
                <div key={i} className="flex justify-between items-center py-3 border-b border-white/7 last:border-b-0 text-[13px]">
                  <span className="text-white/45">{r.key}</span>
                  <span className="font-mono text-white text-[12px]">{r.val}</span>
                </div>
              ))}
              <div className="flex justify-between items-center py-3 text-[13px]">
                <span className="text-white/45">Status</span>
                <span className="bg-tech-cyan/10 border border-tech-cyan/30 text-tech-cyan font-mono text-[11px] px-2 py-0.5">OPEN</span>
              </div>
            </div>
            <a href={loginUrl}
              className="mt-6 inline-flex items-center gap-2 font-bold text-[14px] bg-tech-blue text-white px-6 py-3 rounded-lg hover:bg-tech-blue-b transition-all no-underline">
              Login to Portal <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="bg-tech-bg border-t border-white/7 py-18 px-13 relative z-10">
        <div className="max-w-6xl mx-auto grid md:grid-cols-4 gap-16 mb-14">
          <div>
            <div className="font-mono font-bold text-[16px] text-white mb-1.5">{school.name}</div>
            <div className="font-mono text-[10px] text-tech-blue-b tracking-[0.1em] mb-4">
              // {school.board} AFFILIATED · EST. {school.establishedYear}
            </div>
            <p className="text-[14px] text-white/20 leading-[1.8] max-w-[260px] mb-4">
              Building India's next generation of engineers, scientists, and innovators.
            </p>
            <div className="space-y-1.5 text-[13px] text-white/20">
              <div className="flex items-start gap-2"><MapPin size={12} className="mt-0.5 shrink-0" />{school.address}</div>
              <div className="flex items-center gap-2"><Phone size={12} />{school.phone}</div>
            </div>
          </div>
          {[
            { h: 'Programmes', l: ['Early STEM', 'Tech Core', 'Board Prep', 'Senior Streams', 'Robotics Club'] },
            { h: 'Facilities',  l: ['AI Lab', 'Robotics', 'Science Labs', 'Library', 'Sports'] },
            { h: 'Connect',    l: ['Admissions', 'Student Portal', 'Parent Login', 'Contact', 'Careers'] },
          ].map(col => (
            <div key={col.h}>
              <div className="font-mono text-[11px] text-white/20 tracking-[0.12em] uppercase mb-5">{col.h}</div>
              <ul className="space-y-3 list-none">
                {col.l.map(l => (
                  <li key={l}><a href="#contact" className="text-[14px] text-white/30 hover:text-tech-cyan transition-colors no-underline">{l}</a></li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="border-t border-white/7 pt-7 flex flex-col md:flex-row justify-between font-mono text-[11px] text-white/20 tracking-[0.05em]">
          <span>© {new Date().getFullYear()} {school.name}. All rights reserved.</span>
          <span>{school.affiliation}</span>
        </div>
      </footer>
    </div>
  );
}